import { api } from "../utils";
import { has, isEmpty } from "lodash";
import * as types from "./types";

export default {
  /**
   * Load the course info and progress into the internal store
   */
  [types.COURSE_INIT]: async function({ commit, state }) {
    commit(types.LOADING_START);

    const response = await api.get("course_list");

    commit(types.LOADING_STOP);

    if (!has(response, "data.data.modules")) {
      throw new Error("Invalid course payload received.");
    }

    const { progress, modules } = response.data.data;

    commit(types.COURSE_UPDATE, { modules });

    const module = state.modules.find(module => module.number === progress.module);

    const level = module.levels.find(level => level.number === progress.level);

    commit(types.PROGRESS_UPDATE, { module, level });
  },

  /**
   * Tell the backend about the latest module and level available to the user
   */
  [types.PROGRESS_RECORD]: async function({ commit, getters }, { module, level }) {
    if (getters.isBefore(level)) {
      // the user is revisiting a previous level; no need to update the progress
      return;
    }

    // update the internal store
    commit(types.PROGRESS_UPDATE, { module, level });

    if (Number(process.env.VUE_APP_TRACK_PROGRESS) === 0) {
      return;
    }

    commit(types.LOADING_START);

    try {
      await api.patch("user_progress", {
        module: module.number,
        level: level.number,
      });
    } finally {
      commit(types.LOADING_STOP);
    }
  },

  /**
   * Track progress async and open the level detail view
   */
  [types.LEVEL_OPEN]: async function({ getters, dispatch, commit }, { level }) {
    try {
      // grab the level details
      await dispatch(types.LEVEL_FETCH, { level });

      // set the level details in the store
      commit(types.COURSE_SET_LEVEL, { id: level.node_id });

      const { submission_token } = getters.getLevelByNodeId(level.node_id);

      // flush all responses, setting a submission token from the level detail if available
      commit(types.RESPONSES_RESET, { submission_token });
    } catch (error) {
      console.error(error);
      console.warn("Not opening level details; no details could be fetched");
    } finally {
      const refreshedLevel = getters.getLevelByNodeId(level.node_id);

      await dispatch(types.PROGRESS_RECORD, {
        level: refreshedLevel,
        module: getters.getModuleFor(refreshedLevel),
      });
    }
  },

  [types.LEVEL_FETCH]: async function({ commit }, { level }) {
    if (level.fetched) {
      console.info(`Details for level with id [${level.node_id}] have already been fetched. Skipping.`);
      // there is no need to re-fetch level details
      return;
    }

    commit(types.LOADING_START);
    commit(types.LEVEL_UPDATE, {
      id: level.node_id,
      payload: { loading: true },
    });

    try {
      const response = await api.get(`level_details/${level.node_id}`);

      commit(types.LEVEL_UPDATE, {
        id: level.node_id,
        payload: { ...response.data.data, fetched: true },
      });
    } catch (error) {
      console.warn("Could not fetch level details");
      throw error;
    } finally {
      commit(types.LEVEL_UPDATE, {
        id: level.node_id,
        payload: { loading: false },
      });
      commit(types.LOADING_STOP);
    }
  },

  async [types.LEVEL_CLOSE]({ commit, getters }, { submit = false } = {}) {
    const payload = getters.getResponseSubmission;
    const close = () => commit(types.COURSE_SET_LEVEL, { id: null });

    if (!submit || isEmpty(payload.submission_token)) {
      close();

      return;
    }

    try {
      await api.post("user_submission", payload);

      commit(types.RESPONSES_RESET);
    } catch (error) {
      if (!error.response || !error.response.status) {
        throw error; // rethrow when the error was not an axios bad response
      }

      console.error("SUBMISSION FAILED", error.message);
    } finally {
      close();
    }
  },
};
